/**
 * Tiny per-isolate memo for public, anon-readable catalogue reads made during SSR.
 *
 * - Fresh for `ttlMs`. After that the last good value is returned *immediately*
 *   and one background refresh is started (stale-while-revalidate), so a slow
 *   database never blocks a render that already has a known-good answer.
 * - Concurrent callers share one in-flight request.
 * - Each fetch is bounded by `timeoutMs`. On timeout/error, or when `isValid`
 *   rejects the result (e.g. an empty catalogue), the last good value is kept.
 * - No timers survive a call, so the build-time prerender pass can exit.
 *
 * Only for data that is identical for every visitor. Never cache per-user data.
 */
type Entry<T> = { value?: T; at: number; inflight?: Promise<T | undefined> };
const store = new Map<string, Entry<unknown>>();

export interface MemoOptions<T> {
  /** Reject a result as "not truth" (kept out of the cache; previous value wins). */
  isValid?: (value: T) => boolean;
}

export async function memoPublic<T>(
  key: string,
  ttlMs: number,
  timeoutMs: number,
  load: () => Promise<T>,
  options: MemoOptions<T> = {},
): Promise<T | undefined> {
  const entry = (store.get(key) as Entry<T> | undefined) ?? { at: 0 };
  store.set(key, entry);
  const hasValue = entry.value !== undefined;
  if (hasValue && Date.now() - entry.at < ttlMs) return entry.value;

  if (!entry.inflight) {
    entry.inflight = (async () => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const value = await Promise.race([
          load(),
          new Promise<never>((_, reject) => {
            timer = setTimeout(() => reject(new Error("timeout")), timeoutMs);
          }),
        ]);
        if (options.isValid && !options.isValid(value)) return entry.value;
        entry.value = value;
        entry.at = Date.now();
        return value;
      } catch {
        return entry.value; // stale-on-error, or undefined
      } finally {
        if (timer) clearTimeout(timer);
        entry.inflight = undefined;
      }
    })();
  }
  // Stale value available: answer now, the refresh finishes in the background.
  if (hasValue) {
    entry.inflight.catch(() => undefined);
    return entry.value;
  }
  return entry.inflight;
}

/** Resolves to `fallback` if `p` has not settled within `ms` (or rejects). */
export async function withBudget<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      p.catch(() => fallback),
      new Promise<T>((resolve) => {
        timer = setTimeout(() => resolve(fallback), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
